/**
 * Parking zone detail (/parking/zones/:id) — V0.3 marquee Tier 1.
 *
 * Header card: zone summary + active/inactive toggle + edit button.
 * Slot grid: visual 64×64 grid of all slots in this zone, colored by status.
 *   green=available, blue=allocated, amber=reserved (visitor-in-use), gray=blocked.
 *   Clicking a slot opens a side drawer with slot detail + quick actions.
 *
 * Backend contracts:
 *   GET    /v1/parking/zones/:id
 *   PATCH  /v1/parking/zones/:id
 *   GET    /v1/parking/slots?zone_id=:id&limit=…
 *   POST   /v1/parking/slots/bulk
 *   POST   /v1/parking/slots
 *   POST   /v1/parking/slots/:id/block | /unblock
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  Layers,
  Plus,
  Upload,
  Edit3,
  Power,
  RotateCcw,
  AlertTriangle,
  ParkingCircle,
  Map as MapIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import type {
  ParkingZone,
  ParkingSlot,
  ParkingSlotOwnership,
  ParkingSlotStatus,
  ParkingSlotType,
  VehicleSizeClass,
  CursorList
} from './_parking-types';
import {
  SLOT_TYPE_LABEL,
  VEHICLE_SIZE_LABEL,
  OWNERSHIP_LABEL,
  STATUS_LABEL,
  STATUS_BADGE
} from './_parking-types';

// ───── status colour palette for the visual grid ─────
//
// Uses CSS vars from the design tokens so light/dark mode picks up the right
// tone automatically. The tile carries the colour as a border so the slot
// number stays legible on the surface.
const STATUS_TILE: Record<ParkingSlotStatus, string> = {
  available: 'border-[color:var(--vt-safe)] bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]',
  allocated: 'border-vt-info bg-[color:var(--vt-info-100)] text-vt-info',
  reserved: 'border-vt-warn bg-[color:var(--vt-warn-100)] text-vt-warn',
  blocked: 'border-vt-border-strong bg-vt-surface-2 text-vt-text-muted'
};

const BulkImportSchema = z
  .object({
    prefix: z.string().trim().min(1, 'Prefix required').max(20),
    from_number: z.coerce.number().int().min(1).max(99_999),
    to_number: z.coerce.number().int().min(1).max(99_999),
    slot_type: z.enum([
      'covered',
      'open',
      'two_wheeler',
      'ev_charging',
      'handicapped',
      'visitor'
    ]),
    vehicle_size: z.enum(['two_wheeler', 'hatchback', 'sedan', 'suv', 'oversized']),
    ownership_kind: z.enum([
      'society_owned',
      'unit_owned',
      'builder_unsold',
      'common_visitor'
    ])
  })
  .refine((v) => v.to_number >= v.from_number, {
    message: 'To-number must be ≥ from-number',
    path: ['to_number']
  });
type BulkImportForm = z.infer<typeof BulkImportSchema>;

const QuickAddSchema = z.object({
  slot_number: z.string().trim().min(1, 'Slot number required').max(40),
  slot_type: z.enum([
    'covered',
    'open',
    'two_wheeler',
    'ev_charging',
    'handicapped',
    'visitor'
  ]),
  vehicle_size: z.enum(['two_wheeler', 'hatchback', 'sedan', 'suv', 'oversized']),
  ownership_kind: z.enum([
    'society_owned',
    'unit_owned',
    'builder_unsold',
    'common_visitor'
  ]),
  monthly_fee_inr: z.coerce.number().int().min(0).max(1_000_000).optional()
});
type QuickAddForm = z.infer<typeof QuickAddSchema>;

export function ParkingZoneDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const zoneQ = useQuery({
    queryKey: ['parking-zones', 'detail', id],
    queryFn: () => get<ParkingZone>(`/parking/zones/${id}`),
    enabled: Boolean(id)
  });
  const zone = zoneQ.data;

  // All slots in this zone (single page; usually < 500).
  const slotsQ = useQuery({
    queryKey: ['parking-slots', 'zone', id],
    queryFn: () =>
      get<CursorList<ParkingSlot>>('/parking/slots', { params: { zone_id: id, limit: 500 } }),
    enabled: Boolean(id)
  });
  const slots = slotsQ.data?.items ?? [];

  // Status counts for the header chip strip.
  const counts = useMemo(() => {
    const c: Record<ParkingSlotStatus, number> = {
      available: 0,
      allocated: 0,
      reserved: 0,
      blocked: 0
    };
    for (const s of slots) c[s.status] += 1;
    return c;
  }, [slots]);

  const occupancyPct =
    slots.length === 0 ? 0 : Math.round(((counts.allocated + counts.reserved) / slots.length) * 100);

  // Active toggle (PATCH zone).
  const toggleActive = useMutation({
    mutationFn: (next: boolean) => patch<ParkingZone>(`/parking/zones/${id}`, { is_active: next }),
    onSuccess: () => {
      toast.success('Zone updated');
      void queryClient.invalidateQueries({ queryKey: ['parking-zones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Quick-add slot in this zone.
  const [addOpen, setAddOpen] = useState(false);
  const addForm = useForm<QuickAddForm>({
    resolver: zodResolver(QuickAddSchema),
    mode: 'onChange',
    defaultValues: {
      slot_number: '',
      slot_type: 'covered',
      vehicle_size: 'sedan',
      ownership_kind: 'society_owned',
      monthly_fee_inr: 0
    }
  });
  useEffect(() => {
    if (addOpen && zone) {
      addForm.reset({
        slot_number: '',
        slot_type: zone.default_slot_type,
        vehicle_size: zone.default_vehicle_size,
        ownership_kind: zone.default_ownership_kind,
        monthly_fee_inr: zone.default_monthly_fee_inr ?? 0
      });
    }
  }, [addOpen, zone]); // eslint-disable-line react-hooks/exhaustive-deps
  const addSlot = useMutation({
    mutationFn: (v: QuickAddForm) =>
      post<ParkingSlot>('/parking/slots', {
        zone_id: id,
        slot_number: v.slot_number,
        slot_type: v.slot_type,
        vehicle_size: v.vehicle_size,
        ownership_kind: v.ownership_kind,
        monthly_fee_inr: v.monthly_fee_inr ?? null
      }),
    onSuccess: () => {
      toast.success('Slot created');
      setAddOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-zones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Bulk import inside this zone.
  const [bulkOpen, setBulkOpen] = useState(false);
  const bulkForm = useForm<BulkImportForm>({
    resolver: zodResolver(BulkImportSchema),
    mode: 'onChange',
    defaultValues: {
      prefix: '',
      from_number: 1,
      to_number: 20,
      slot_type: 'covered',
      vehicle_size: 'sedan',
      ownership_kind: 'society_owned'
    }
  });
  useEffect(() => {
    if (bulkOpen && zone) {
      bulkForm.reset({
        prefix: `${zone.code}-`,
        from_number: 1,
        to_number: 20,
        slot_type: zone.default_slot_type,
        vehicle_size: zone.default_vehicle_size,
        ownership_kind: zone.default_ownership_kind
      });
    }
  }, [bulkOpen, zone]); // eslint-disable-line react-hooks/exhaustive-deps
  const bulkImport = useMutation({
    mutationFn: (v: BulkImportForm) =>
      post<{ created: number }>('/parking/slots/bulk', {
        zone_id: id,
        prefix: v.prefix,
        from_number: v.from_number,
        to_number: v.to_number,
        slot_type: v.slot_type,
        vehicle_size: v.vehicle_size,
        ownership_kind: v.ownership_kind
      }),
    onSuccess: (data) => {
      toast.success(`Created ${data.created} slot${data.created === 1 ? '' : 's'}`);
      setBulkOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-zones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Slot detail drawer.
  const [activeSlot, setActiveSlot] = useState<ParkingSlot | null>(null);

  const blockSlot = useMutation({
    mutationFn: (slotId: string) => post<ParkingSlot>(`/parking/slots/${slotId}/block`, {}),
    onSuccess: () => {
      toast.success('Slot blocked');
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
      setActiveSlot(null);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const unblockSlot = useMutation({
    mutationFn: (slotId: string) => post<ParkingSlot>(`/parking/slots/${slotId}/unblock`, {}),
    onSuccess: () => {
      toast.success('Slot unblocked');
      void queryClient.invalidateQueries({ queryKey: ['parking-slots'] });
      setActiveSlot(null);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  if (zoneQ.isLoading) {
    return (
      <>
        <PageHeader title="Zone" description="Loading…" />
        <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }
  if (zoneQ.isError || !zone) {
    return (
      <>
        <PageHeader title="Zone" description="Could not load this zone." />
        <div className="mx-auto w-full max-w-[1200px] px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Zone not found"
                description={zoneQ.error ? getErrorMessage(zoneQ.error) : 'This zone may have been removed.'}
                action={
                  <Button variant="secondary" onClick={() => void navigate({ to: '/parking' })}>
                    <ArrowLeft className="h-4 w-4" /> Back to parking
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={zone.name}
        description={`Parking zone · ${zone.code}`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/parking' })} title="Back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* Header card */}
        <Card>
          <CardContent className="flex flex-col gap-vt-4 p-vt-5 md:flex-row md:items-start">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
              <Layers className="h-7 w-7" strokeWidth={1.75} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-vt-24 font-semibold tracking-[-0.01em] text-vt-text">
                {zone.name}
              </h2>
              <div className="mt-vt-1 flex flex-wrap items-center gap-vt-3 text-vt-13 text-vt-text-muted">
                <span className="font-mono">{zone.code}</span>
                <span>·</span>
                <span>{zone.total_slots} declared slots</span>
                <span>·</span>
                <span>{slots.length} live</span>
                <span>·</span>
                <span>Default: {SLOT_TYPE_LABEL[zone.default_slot_type]} · {OWNERSHIP_LABEL[zone.default_ownership_kind]}</span>
              </div>
              <div className="mt-vt-2 flex flex-wrap items-center gap-vt-2">
                {zone.is_active ? (
                  <Badge variant="ok">Active</Badge>
                ) : (
                  <Badge variant="muted">Inactive</Badge>
                )}
                <Badge variant="info">Occupancy {occupancyPct}%</Badge>
                <Badge variant="ok">Available {counts.available}</Badge>
                <Badge variant="default">Allocated {counts.allocated}</Badge>
                <Badge variant="warn">Reserved {counts.reserved}</Badge>
                <Badge variant="muted">Blocked {counts.blocked}</Badge>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-vt-2">
              <Button
                size="sm"
                variant={zone.is_active ? 'danger' : 'secondary'}
                onClick={() => toggleActive.mutate(!zone.is_active)}
                loading={toggleActive.isPending}
                loadingText="Saving…"
              >
                {zone.is_active ? (
                  <>
                    <Power className="h-4 w-4" /> Deactivate
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" /> Activate
                  </>
                )}
              </Button>
              <Button size="sm" variant="secondary" asChild>
                <Link to="/parking">
                  <Edit3 className="h-4 w-4" /> Edit zone
                </Link>
              </Button>
              {/* Phase 3 visual map editor — separate page so the existing zone
                  detail surface stays focused on the slot grid + CRUD. */}
              <Button size="sm" asChild title="Open the visual map editor">
                <Link
                  to="/parking/zones/$zoneId/map"
                  params={{ zoneId: zone.id }}
                >
                  <MapIcon className="h-4 w-4" /> Manage map
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Slot grid */}
        <Card>
          <CardContent className="p-vt-5">
            <div className="mb-vt-4 flex flex-wrap items-center justify-between gap-vt-3">
              <div>
                <h3 className="text-vt-16 font-semibold text-vt-text">Slot grid</h3>
                <p className="text-vt-12 text-vt-text-muted">
                  Click any slot for detail and quick actions.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-vt-2">
                <Legend />
                <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)}>
                  <Upload className="h-4 w-4" /> Bulk import
                </Button>
                <Button size="sm" onClick={() => setAddOpen(true)}>
                  <Plus className="h-4 w-4" /> Add slot
                </Button>
              </div>
            </div>

            {slotsQ.isLoading ? (
              <div className="flex flex-wrap gap-vt-2">
                {Array.from({ length: 24 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-16" />
                ))}
              </div>
            ) : slots.length === 0 ? (
              <EmptyState
                icon={<ParkingCircle className="h-6 w-6" />}
                title="No slots yet"
                description="Add slots in this zone — bulk import is the fastest way."
                action={
                  <Button onClick={() => setBulkOpen(true)}>
                    <Upload className="h-4 w-4" /> Bulk import
                  </Button>
                }
              />
            ) : (
              <div
                className="grid gap-vt-2"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))' }}
              >
                {slots.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveSlot(s)}
                    className={`flex h-16 w-full flex-col items-center justify-center gap-0.5 rounded-vt-sm border-2 ${STATUS_TILE[s.status]} px-1 text-center transition-colors duration-vt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400`}
                    title={`${s.slot_number} · ${STATUS_LABEL[s.status]}`}
                    aria-label={`Slot ${s.slot_number}, ${STATUS_LABEL[s.status]}`}
                  >
                    <span className="truncate font-mono text-vt-11 font-semibold leading-none">
                      {s.slot_number}
                    </span>
                    <span className="text-[9px] uppercase tracking-wide leading-none">
                      {STATUS_LABEL[s.status]}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Slot drawer */}
      <Dialog open={Boolean(activeSlot)} onOpenChange={(v) => (v ? null : setActiveSlot(null))}>
        <DialogContent>
          {activeSlot ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  Slot <span className="font-mono">{activeSlot.slot_number}</span>
                </DialogTitle>
                <DialogDescription>
                  {SLOT_TYPE_LABEL[activeSlot.slot_type]} ·{' '}
                  {VEHICLE_SIZE_LABEL[activeSlot.vehicle_size]} ·{' '}
                  {OWNERSHIP_LABEL[activeSlot.ownership_kind]}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-vt-3 py-vt-2 text-vt-13">
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Status</div>
                  <Badge variant={STATUS_BADGE[activeSlot.status]} className="mt-vt-1">
                    {STATUS_LABEL[activeSlot.status]}
                  </Badge>
                </div>
                <div>
                  <div className="text-vt-12 text-vt-text-muted">Monthly fee</div>
                  <div className="font-mono text-vt-text">
                    {activeSlot.monthly_fee_inr
                      ? `₹${activeSlot.monthly_fee_inr.toLocaleString('en-IN')}`
                      : '—'}
                  </div>
                </div>
                {activeSlot.owner_unit ? (
                  <div className="col-span-2">
                    <div className="text-vt-12 text-vt-text-muted">Owner unit</div>
                    <div className="text-vt-text">
                      {activeSlot.owner_unit.block?.name
                        ? `${activeSlot.owner_unit.block.name} · `
                        : ''}
                      {activeSlot.owner_unit.number}
                    </div>
                  </div>
                ) : null}
                {activeSlot.notes ? (
                  <div className="col-span-2">
                    <div className="text-vt-12 text-vt-text-muted">Notes</div>
                    <p className="whitespace-pre-wrap text-vt-text">{activeSlot.notes}</p>
                  </div>
                ) : null}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setActiveSlot(null)}>
                  Close
                </Button>
                {activeSlot.status === 'blocked' ? (
                  <Button
                    variant="secondary"
                    onClick={() => unblockSlot.mutate(activeSlot.id)}
                    loading={unblockSlot.isPending}
                    loadingText="Saving…"
                  >
                    <RotateCcw className="h-4 w-4" /> Unblock
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    onClick={() => blockSlot.mutate(activeSlot.id)}
                    loading={blockSlot.isPending}
                    loadingText="Saving…"
                  >
                    <Power className="h-4 w-4" /> Block
                  </Button>
                )}
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Quick add slot */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add slot in {zone.name}</DialogTitle>
            <DialogDescription>Single slot. Use bulk import for ranges.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={addForm.handleSubmit((v) => addSlot.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="qa-num">Slot number</Label>
              <Input id="qa-num" placeholder={`${zone.code}-001`} {...addForm.register('slot_number')} />
              {addForm.formState.errors.slot_number ? (
                <p className="text-vt-12 text-vt-alert">{addForm.formState.errors.slot_number.message}</p>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="qa-type">Type</Label>
                <Select id="qa-type" {...addForm.register('slot_type')}>
                  {(Object.keys(SLOT_TYPE_LABEL) as ParkingSlotType[]).map((k) => (
                    <option key={k} value={k}>
                      {SLOT_TYPE_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="qa-size">Size</Label>
                <Select id="qa-size" {...addForm.register('vehicle_size')}>
                  {(Object.keys(VEHICLE_SIZE_LABEL) as VehicleSizeClass[]).map((k) => (
                    <option key={k} value={k}>
                      {VEHICLE_SIZE_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="qa-own">Ownership</Label>
                <Select id="qa-own" {...addForm.register('ownership_kind')}>
                  {(Object.keys(OWNERSHIP_LABEL) as ParkingSlotOwnership[]).map((k) => (
                    <option key={k} value={k}>
                      {OWNERSHIP_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="qa-fee">Monthly fee (₹)</Label>
              <Input id="qa-fee" type="number" min={0} {...addForm.register('monthly_fee_inr')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={addSlot.isPending}
                loadingText="Saving…"
                disabled={addSlot.isPending || !addForm.formState.isValid}
              >
                Create slot
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk import */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk import slots in {zone.name}</DialogTitle>
            <DialogDescription>
              Generates <span className="font-mono">PREFIX-N</span> for every N in [from, to].
              Existing numbers are skipped server-side.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={bulkForm.handleSubmit((v) => bulkImport.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="bz-prefix">Prefix</Label>
                <Input id="bz-prefix" {...bulkForm.register('prefix')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="bz-from">From</Label>
                <Input id="bz-from" type="number" min={1} {...bulkForm.register('from_number')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="bz-to">To</Label>
                <Input id="bz-to" type="number" min={1} {...bulkForm.register('to_number')} />
                {bulkForm.formState.errors.to_number ? (
                  <p className="text-vt-12 text-vt-alert">{bulkForm.formState.errors.to_number.message}</p>
                ) : null}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="bz-type">Type</Label>
                <Select id="bz-type" {...bulkForm.register('slot_type')}>
                  {(Object.keys(SLOT_TYPE_LABEL) as ParkingSlotType[]).map((k) => (
                    <option key={k} value={k}>
                      {SLOT_TYPE_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="bz-size">Size</Label>
                <Select id="bz-size" {...bulkForm.register('vehicle_size')}>
                  {(Object.keys(VEHICLE_SIZE_LABEL) as VehicleSizeClass[]).map((k) => (
                    <option key={k} value={k}>
                      {VEHICLE_SIZE_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="bz-own">Ownership</Label>
                <Select id="bz-own" {...bulkForm.register('ownership_kind')}>
                  {(Object.keys(OWNERSHIP_LABEL) as ParkingSlotOwnership[]).map((k) => (
                    <option key={k} value={k}>
                      {OWNERSHIP_LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setBulkOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={bulkImport.isPending}
                loadingText="Importing…"
                disabled={bulkImport.isPending || !bulkForm.formState.isValid}
              >
                <Upload className="h-4 w-4" /> Import
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Legend(): JSX.Element {
  const items: Array<{ status: ParkingSlotStatus; label: string }> = [
    { status: 'available', label: 'Available' },
    { status: 'allocated', label: 'Allocated' },
    { status: 'reserved', label: 'Reserved' },
    { status: 'blocked', label: 'Blocked' }
  ];
  return (
    <div className="flex items-center gap-vt-3 rounded-vt-sm border border-vt-border bg-vt-surface-2/40 px-vt-3 py-vt-1 text-vt-11 text-vt-text-muted">
      {items.map((it) => (
        <span key={it.status} className="inline-flex items-center gap-1">
          <span className={`inline-block h-3 w-3 rounded-vt-sm border-2 ${STATUS_TILE[it.status]}`} />
          {it.label}
        </span>
      ))}
    </div>
  );
}
